import { useCallback } from 'react'

import { useHistory, useLocation } from 'react-router-dom'

import { useAnswers } from 'providers/AnswersProvider'

import { DOCUMENT_PATH_PREFIX } from '../config/flow'
import { paths, routes } from '../config/routes'
import { useFlowEngine } from '../providers/FlowEngineProvider'
import { usePatient } from '../providers/PatientProvider'

const routesValues = Object.values(routes)

const STEP_EXIT_PATHS: ReadonlySet<string> = new Set([paths.summary, paths.factoringAgreement])

const isStepExitPath = (pathname: string): boolean =>
  STEP_EXIT_PATHS.has(pathname) || pathname.startsWith(`${DOCUMENT_PATH_PREFIX}/`)

const useNavigation = (): {
  goToNextStep: (queryString?: string) => Promise<void>
  goToPreviousStep: () => void
  getCurrentRoute: () => { path: string } | undefined
} => {
  const location = useLocation()
  const history = useHistory()
  const patient = usePatient()
  const answers = useAnswers()
  const flowEngine = useFlowEngine()

  const getCurrentRoute = useCallback(
    () => routesValues.find(({ path }) => path === location.pathname),
    [location?.pathname]
  )

  const goToNextStep = useCallback(
    async (queryString?: string) => {
      const pathname = location.pathname

      if (pathname === paths.intro) {
        const next = flowEngine.getNextEntryPath(null) ?? paths.success
        history.push({ pathname: next, search: queryString })
        return
      }

      if (isStepExitPath(pathname)) {
        const stepName = flowEngine.resolveStepName(pathname)

        if (stepName) {
          const next = (await flowEngine.advance(stepName)) ?? paths.success
          history.push({ pathname: next, search: queryString })
          return
        }
      }

      const current = getCurrentRoute()
      const next = current?.getNextRoute?.(patient?.data?.anamnesisState || [], answers) || current?.next

      if (next) {
        history.push({
          pathname: next,
          search: queryString,
        })
      }
    },
    [history, getCurrentRoute, patient, answers, location.pathname, flowEngine]
  )

  const goToPreviousStep = useCallback(() => {
    const pathname = location.pathname

    if (isStepExitPath(pathname) || pathname === paths.personalInformation) {
      const stepName = flowEngine.resolveStepName(pathname)

      if (stepName) {
        const previous = flowEngine.getPreviousEntryPath(stepName) ?? paths.intro
        history.push({ pathname: previous })
        return
      }
    }

    const current = getCurrentRoute()
    const previous = current?.getPreviousRoute?.(patient?.data?.anamnesisState || [], answers) || current?.previous

    if (previous) {
      return history.push({
        pathname: previous,
      })
    }
  }, [history, getCurrentRoute, patient, answers, location.pathname, flowEngine])

  return {
    goToNextStep,
    goToPreviousStep,
    getCurrentRoute,
  }
}

export default useNavigation
